import { Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup, ValidationErrors,
  Validators
} from '@angular/forms';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { format, parseISO } from 'date-fns';

import { CurrentUser } from '@sondermind/data-access/client-user';
import {
  ALPHANUMERIC_WITH_SPACE_AND_HYPHEN_PATTERN,
  FormControls,
  PartialForm,
  invalidValueValidator,
  BirthdayValidator
} from '@sondermind/utilities/forms';
import { ProviderNetworkCompany } from '@sondermind/utilities/models-client-facing';
import { ClientInsurance, ICostEstimateContextAttributes } from '@sondermind/utilities/models-client-insurance';
import { AccountSetupService } from '@sondermind/utilities/services';

import { toTimeZoneDateTime } from '@sondermind/utilities/time-and-date';

import { ConditionalValidator } from '@sondermind/core/models/validators/conditional.validator';

import { IInsuranceFormData } from '../models/insurance-form-data.interface';
import { ClientInsuranceHttpService } from './client-insurance.httpservice';

export const OTHER_INSURANCE_VALUE = '__otherInsurance';

@Injectable({
  providedIn: 'root'
})
export class InsuranceForm extends PartialForm<IInsuranceFormData, ClientInsurance> {
  bvFailedId: number = null;
  policyHolderSelf: number = -1;

  constructor(
    private builder: UntypedFormBuilder,
    private currentUser: CurrentUser,
    private insuranceHttp: ClientInsuranceHttpService,
    private accountSetupService: AccountSetupService
  ) {
    super();
  }

  get selfPaid(): boolean {
    return this.value.self_pay;
  }

  get selectedPayer(): ProviderNetworkCompany {
    return this.otherSelected ? null : this.value.provider_network_company;
  }

  get otherSelected(): boolean {
    return <any> this.value.provider_network_company === OTHER_INSURANCE_VALUE;
  }

  get failedBV(): boolean {
    return this.bvFailedId != null && !this.selfPaid;
  }

  get groupNumber(): string {
    return this.value.has_no_group_number ? '001' : this.value.group_number;
  }

  build(): UntypedFormGroup {
    this.bvFailedId = null;
    super.resetSubscription();
    const noOther = invalidValueValidator(OTHER_INSURANCE_VALUE);

    const whenInsurance = ConditionalValidator.whenEq('self_pay', false);
    const whenNotPolicyHolder = ConditionalValidator.whenEq('policy_holder', false);
    const whenAuthNumberRequired = ConditionalValidator.when<ProviderNetworkCompany>(
      'provider_network_company', (v) => !!v?.authNumberRequired
    );

    const { required } = Validators;
    const requiredWhenInsurance = whenInsurance(Validators.required);
    const groupAndPolicyPattern = Validators.pattern(ALPHANUMERIC_WITH_SPACE_AND_HYPHEN_PATTERN);
    const authNumberPattern = Validators.pattern(/[0-9]{9}/);
    const validateAuthUnique = this.validateAuthUnique.bind(this);
    const validatePolicyGroupUnique = this.validatePolicyGroupNumUnique.bind(this);
    const hasCashPref = !!this.currentUser.user.hasCashPref;

    // Use insurance state if available, otherwise fall back to mailing address state
    const defaultInsuranceState = this.currentUser.user.insuranceState ||
      this.currentUser.user.profile?.mailingAddress?.state || null;

    const form = this.builder.group(<FormControls<IInsuranceFormData>>{
      self_pay: this.builder.control(hasCashPref),

      insurance_state: this.builder.control(defaultInsuranceState, [requiredWhenInsurance]),
      provider_network_company: this.builder.control(null, [requiredWhenInsurance, noOther]),
      group_number: this.builder.control(null, [
        requiredWhenInsurance,
        groupAndPolicyPattern,
        validatePolicyGroupUnique
      ]),
      has_no_group_number: this.builder.control(false),
      policy_number: this.builder.control(null, [
        requiredWhenInsurance,
        groupAndPolicyPattern,
        Validators.minLength(2),
        validatePolicyGroupUnique
      ]),
      auth_number: this.builder.control(null, [
        whenAuthNumberRequired(whenInsurance(required)),
        authNumberPattern,
        validateAuthUnique
      ]),
      is_tricare_dependent: this.builder.control(false),

      policy_holder: this.builder.control(false, [requiredWhenInsurance]),
      policy_holder_relationship_id: this.builder.control(null, [requiredWhenInsurance]),
      patient_relationship_type_id: this.builder.control(null, [whenInsurance(whenNotPolicyHolder(required))]),

      front_card_uri: this.builder.control(null, [requiredWhenInsurance]),
      back_card_uri: this.builder.control(null, [requiredWhenInsurance]),

      first_name: this.builder.control(null, [requiredWhenInsurance]),
      last_name: this.builder.control(null, [requiredWhenInsurance]),
      date_of_birth: this.builder.control(null, [
        requiredWhenInsurance,
        BirthdayValidator.validate
      ]),
      persona_provider_id: this.builder.control(null),
      date_of_service: this.builder.control(null),
      session_length: this.builder.control(null),
      visit_type: this.builder.control(null)
    });

    // watch self pay, and recalculate validation if self pay changes
    // since nearly everything is dependent on !self_pay for validation
    this.subscription.add(
      form.controls['self_pay'].valueChanges.subscribe(() => {
        const keys = Object.keys(form.controls);
        keys.forEach((k) => {
          // don't infinite loop please
          if (k === 'self_pay') { return }

          // don't fire an event, we do it in a batch on the form at the end
          form.controls[k].updateValueAndValidity({ emitEvent: false });
        });

        // we didn't fire events or set the form level validity, do that as a batch
        form.updateValueAndValidity();
      })
    );

    // watch group and policy, to update validity of auth number
    this.subscription.add(
      form.controls['group_number'].valueChanges.subscribe(() => form.controls['auth_number'].updateValueAndValidity())
    );

    this.subscription.add(
      form.controls['policy_number'].valueChanges.subscribe(() => form.controls['auth_number'].updateValueAndValidity())
    );

    // watch policy holder to default some fields
    // distinct, because we only want to deal with actual transitions, otherwise we clear fields we shouldnt
    this.subscription.add(
      form.controls['policy_holder_relationship_id'].valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((v) => this.changePolicyHolder(v, form))
    );

    this.subscription.add(
      form.controls['has_no_group_number'].valueChanges.subscribe((newValue) => {
        if (newValue === true) {
          form.get('group_number').setValue(null);
          form.get('group_number').disable();
        } else {
          form.get('group_number').enable();
        }
      })
    );

    // initialize policy holder side-effects
    this.changePolicyHolder(null, form);

    return form;
  }

  override enable(): void {
    super.enable();

    // because the policy holder is conditionally disabled, we need to reassert the enable/disable state,
    // otherwise, it becomes enabled due to the form-level enabling
    this.changePolicyHolder(this.value.policy_holder_relationship_id, this.form);
  }

  private validateAuthUnique(control: AbstractControl): ValidationErrors | null {
    if (!control.parent) {
      return null;
    }

    const group = this.groupNumber;
    const policy = control.parent.get('policy_number').value;
    const auth = control.value;

    // eslint-disable-next-line eqeqeq
    if (auth && (auth === group || auth == policy)) {
      return { authNotUnique: true };
    }

    return null;
  }

  private validatePolicyGroupNumUnique(control: AbstractControl): ValidationErrors | null {
    if (!control.parent) {
      return null;
    }

    const group = this.groupNumber;
    const policy = control.parent.get('policy_number').value;

    if (group && (group === policy)) {
      return { policyOrGroupNotUnique: true };
    }

    return null;
  }

  private changePolicyHolder(policyHolderRelationshipId: number, form: UntypedFormGroup): void {
    const { firstName, lastName, birthDate } = this.currentUser.user.profile;
    const dob = birthDate || null;
    // when the selected `policy_holder_relationship_id` is -1, the client is the policyholder
    // in this scenario, set `patient_relationship_type_id` to null and `policy_holder` to true
    if (policyHolderRelationshipId === this.policyHolderSelf) {
      // Format the birthdate if it exists and is in ISO format
      let formattedDob = dob;
      if (dob) {
        // Extract just the date part (YYYY-MM-DD) from ISO datetime strings to avoid timezone issues
        const dateOnly = dob.includes('T') ? dob.split('T')[0] : dob;
        if (dateOnly.includes('-') && dateOnly.length === 10) {
          formattedDob = format(parseISO(dateOnly), 'MM/dd/yyyy');
        }
      }

      form.patchValue({
        first_name: firstName,
        last_name: lastName,
        date_of_birth: formattedDob,
        policy_holder: true,
        patient_relationship_type_id: null
      });
    } else {
      // when the selected `policy_holder_relationship_id` is not -1, the client is not the policyholder
      // the `policy_holder_relationship_id` is a valid ID for `patient_relationship_type_id`
      // set `patient_relationship_type_id` to the valid ID and set `policy_holder` to false
      form.patchValue({
        policy_holder: false,
        patient_relationship_type_id: policyHolderRelationshipId
      });

      const birthDateComparison = dob === form.value.date_of_birth;
      const wasPolicyHolder = firstName === form.value.first_name
        && lastName === form.value.last_name
        && birthDateComparison;

      // only reset the policy holder values if we were previously the policy holder,
      // meaning that the data is easy to get back (ex: mistakenly selected spouse instead of self)
      // otherwise leave it, because they changed it.
      if (wasPolicyHolder) {
        form.patchValue({
          first_name: '',
          last_name: '',
          date_of_birth: ''
        });
      }
    }

    form.updateValueAndValidity();
  }

  save(value: IInsuranceFormData): Observable<ClientInsurance> {
    if (value.self_pay) {
      return this.saveSelfPay();
    }

    return this.saveInsurance(value);
  }

  saveSelfPay(): Observable<ClientInsurance> {
    const reqs = [
      this.insuranceHttp.addCashPay().pipe(this.applyErrorsToForm())
    ];

    // possibly delete the failed insurance from before
    if (this.bvFailedId) {
      reqs.push(this.insuranceHttp.delete(this.bvFailedId));
    }

    // possibly more than one request, but we only care about return from addCashPay
    return forkJoin(reqs).pipe(
      map(([cashPay]) => cashPay)
    );
  }

  saveInsurance(value: IInsuranceFormData): Observable<ClientInsurance> {
    // reshape the incoming value into request parameters
    const dateOfBirth = value.date_of_birth
      ? toTimeZoneDateTime(new Date(value.date_of_birth), this.currentUser.user.profile.timeZone).toJSDate()
      : null;
    const insuranceInfo = {
      provider_network_company_id: value.provider_network_company.id,
      group_number: this.groupNumber,
      policy_number: value.policy_number,
      auth_number: value.auth_number,

      patient_relationship_type_id: value.patient_relationship_type_id,
      dependent: !value.policy_holder,

      front_card_uri: value.front_card_uri,
      back_card_uri: value.back_card_uri,

      first_name: value.first_name,
      last_name: value.last_name,
      date_of_birth: dateOfBirth
    };

    // Determine whether or not we need to include cost estimate context
    const costEstimateContext: { [key: string]: ICostEstimateContextAttributes; } = {
      session_cost_estimate_params: {
        persona_provider_id: value.persona_provider_id,
        date_of_service: value.date_of_service,
        session_length: value.session_length,
        visit_type: value.visit_type
      }
    };

    const mergedInsuranceInfo = this.costEstimateContextSet(costEstimateContext['session_cost_estimate_params']) ?
      { ...insuranceInfo, ...costEstimateContext } : insuranceInfo;

    // if we've already submitted and failed for BV failure,
    // try and update the failed one instead
    // otherwise, create a new insurance object
    const options = { view: 'with_create_update_bv_info' };
    const request: Observable<ClientInsurance> = this.bvFailedId
      ? this.insuranceHttp.update(this.bvFailedId, mergedInsuranceInfo, this.accountSetupService.context, options)
      : this.insuranceHttp.create(mergedInsuranceInfo, this.accountSetupService.context, options);

    // behavior for success is conditional on whether we've failed bv
    // if we've already failed bv, we'll allow the save to continue - only failing once
    // if we haven't already failed bv, we'll add an error to the form and fail out
    return request.pipe(
      switchMap((data) => {
        const alreadyFailed = this.bvFailedId != null;
        const verified = data.recent_benefit_verification && data.recent_benefit_verification.aasm_state === 'verified';

        // if we're confirming after successfully verified, save
        if (verified) {
          this.bvFailedId = null;
          return of(data);
        }

        // Check for coverage date issues first - treat as success so they can be handled by UI
        const hasCoverageDates = data.recent_benefit_verification?.coverage_start_date ||
                                data.recent_benefit_verification?.coverage_end_date;

        if (hasCoverageDates) {
          return of(data);
        }

        const hasPayorDowntime = data.recent_benefit_verification?.failure_reason === 'payor_downtime';

        if (hasPayorDowntime) {
          return of(data);
        }

        const hasMedicaidPlan = data.recent_benefit_verification?.failure_reason === 'medicaid_plan';

        if (hasMedicaidPlan) {
          return of(data);
        }

        // Check if BV failed but has suggested payer information
        // suggested_payer is embedded as a string within the error message, not in structured details
        const hasSuggestedPayer = data.recent_benefit_verification?.error?.some((error) => {
          const errorString = typeof error === 'string' ? error : error.message || '';
          return errorString.includes('suggested_payer ');
        });

        if (hasSuggestedPayer) {
          // Don't set bvFailedId - treat as success with suggested payer info
          return of(data);
        }

        const hasUnknownCostshare = data.recent_benefit_verification?.error?.some((error) => {
          const errorString = typeof error === 'string' ? error : error.message || '';
          return errorString.includes('UNKNOWN_COSTSHARE');
        });

        if (hasUnknownCostshare) {
          return of(data);
        }

        const hasNoCoverage = data.recent_benefit_verification?.['coverage_status'] === 'inactive' ||
                             data.recent_benefit_verification?.['coverage_status'] === 'unknown';
        const hasNoErrors = !data.recent_benefit_verification?.error ||
                           data.recent_benefit_verification.error.length === 0;

        if (hasNoCoverage && hasNoErrors) {
          // Don't set bvFailedId - treat as success with no coverage info
          return of(data);
        }

        this.bvFailedId = data.id;

        // parent form listens for bvFailed to do button stuff
        // bvFailed keeps track of # of attempts
        return throwError({
          bvFailed: alreadyFailed ? 2 : 1,
          bvFailedId: this.bvFailedId,
          errors: { base: ['Failed Benefit Verification'] }
        });
      }),
      this.markSuccessfulSave(),
      this.applyErrorsToForm()
    );
  }

  private costEstimateContextSet(contextObj: ICostEstimateContextAttributes): boolean {
    return Object.values(contextObj).every((value) => value !== null && value !== undefined);
  }
}
